import type { Policy, PolicySection } from '../../../../types';

export type ContentData = {
  anchor?: string | null;
  content: string;
  depth: number;
  id: number | string;
  shortDescription?: string | null;
  title?: string | null;
  titleLevel?: number | null;
  type: 'content' | 'shortContent' | 'title';
};

export type GroupedContentData = ContentData | ContentData[];

export function flatten(section: Policy | PolicySection, depth = 0): ContentData[] {
  let contentArray: ContentData[] = [];

  if (section.title) {
    contentArray.push({
      type: 'title',
      content: section.title,
      depth: section.titleLevel ? section.titleLevel : depth,
      id: section.id,
      anchor: section.anchor,
    });
  }

  if (section.shortDescription) {
    contentArray.push({
      type: 'shortContent',
      content: section.shortDescription,
      depth,
      id: section.id,
      anchor: section.anchor,
    });
  }

  if (section.content) {
    contentArray.push({
      type: 'content',
      content: section.content,
      depth,
      id: section.id,
      anchor: section.anchor,
    });
  }

  section.sections?.forEach((subsection) => {
    contentArray = [...contentArray, ...flatten(subsection, depth + 1)];
  });

  return contentArray;
}

export function group(
  flattenedContentData: ContentData[],
  key: keyof ContentData,
  regValue: RegExp
): GroupedContentData[] {
  const groupedData: GroupedContentData[] = [];
  let items: ContentData[] = [];

  flattenedContentData.forEach((content) => {
    const value = content[key];

    if (String(value).match(regValue)) {
      items.push(content);
      return;
    }

    if (items.length > 0) {
      groupedData.push(items);
      items = [];
    }

    groupedData.push(content);
  });

  if (items.length > 0) {
    groupedData.push(items);
  }

  return groupedData;
}

export function addTitleLevel(flattenedContentData: ContentData[]): ContentData[] {
  return flattenedContentData.map((item) => {
    if (item.type !== 'title' || !item.content.startsWith('<')) {
      return item;
    }

    const headingLevelMatch = item.content.match(/<h(\d)>/);

    if (!headingLevelMatch) {
      return item;
    }

    const level = Number(headingLevelMatch[1]);
    const newContent = item.content.replace(/<.*?>/g, '');

    return {
      ...item,
      content: newContent,
      depth: level - 1,
    };
  });
}
